import { formatDate } from "@plasma/utils/time";

import type {
  BusinessReportData,
  ReasonTracking,
  SalesSummary,
  TransactionSummary,
  VisibilitySettings,
} from "./business-report-types";
import {
  calculateItemsTotal,
  computeVisibility,
} from "./business-report-types";

// Report Header Component
function ReportHeader({
  locationName,
  address,
  currentDate,
  isPreviewData,
  isLoadingReport,
  reportError,
  hasRealData,
}: {
  locationName: string;
  address: {
    addressLine1: string;
    addressLine2: string | null;
    city: string;
    state: string;
    zipCode: number;
  } | null;
  currentDate: Date;
  isPreviewData: boolean;
  isLoadingReport: boolean;
  reportError: Error | null;
  hasRealData: boolean;
}) {
  return (
    <div className="text-center">
      END OF DAY REPORT: BUSINESS
      <br />
      {locationName}
      <br />
      {address ? (
        <>
          {address.addressLine1}
          {address.addressLine2 && `, ${address.addressLine2}`}
          <br />
          {address.city}, {address.state} {address.zipCode}
        </>
      ) : (
        "No address provided"
      )}
      <br />
      {formatDate(currentDate || new Date(), "MMMM Do, YYYY")}
      {isPreviewData && (
        <div className="caption bg-warning/10 text-warning mt-2 rounded-sm px-2 py-1">
          No data found for this date.
        </div>
      )}
      {isLoadingReport && (
        <div className="caption bg-info/10 text-info mt-2 rounded-sm px-2 py-1">
          Loading report data...
        </div>
      )}
      {reportError && (
        <div className="caption bg-destructive/10 text-destructive mt-2 rounded-sm px-2 py-1">
          Error loading report: Failed to load report
        </div>
      )}
      {hasRealData && !isLoadingReport && !reportError && (
        <div className="caption bg-success/10 text-success mt-2 rounded-sm px-2 py-1">
          Showing actual data
        </div>
      )}
    </div>
  );
}

// Orders and Bills Summary Section
function OrdersBillsSummarySection({
  ordersCreated,
  billsClosed,
  showDividers,
}: {
  ordersCreated: number;
  billsClosed: number;
  showDividers: boolean;
}) {
  return (
    <div>
      <div className="text-center font-bold">ORDERS AND BILLS SUMMARY</div>
      <div className="flex justify-between">
        <span>Orders Taken</span> <span>{ordersCreated}</span>
      </div>
      <div className="flex justify-between">
        <span>Bills Processed</span> <span>{billsClosed}</span>
      </div>
      {showDividers && <hr className="border-border my-2 border-t" />}
    </div>
  );
}

// Sales Categories Section
function SalesCategoriesSection({
  salesByCategory,
  showDividers,
}: {
  salesByCategory: SalesSummary[];
  showDividers: boolean;
}) {
  return (
    <div>
      <div className="text-center font-bold">SALES CATEGORIES</div>
      {salesByCategory.map((category: SalesSummary) => (
        <div className="flex justify-between" key={category.categoryId}>
          <span>{category.categoryName}</span>
          <span>${category.amount}</span>
        </div>
      ))}
      {showDividers && <hr className="border-border my-2 border-t" />}
    </div>
  );
}

// Sales and Taxes Summary Section
function SalesTaxesSummarySection({
  totalSales,
  totalTaxes,
  showDividers,
}: {
  totalSales: string;
  totalTaxes: string;
  showDividers: boolean;
}) {
  return (
    <div>
      <div className="text-center font-bold">SALES AND TAXES SUMMARY</div>
      <div className="flex justify-between">
        <span>Total Sales</span> <span>${totalSales}</span>
      </div>
      <div className="flex justify-between">
        <span>Total Taxes</span> <span>${totalTaxes}</span>
      </div>
      {showDividers && <hr className="border-border my-2 border-t" />}
    </div>
  );
}

// Device Transaction Section
function DeviceTransactionSection({
  device,
  index,
}: {
  device: TransactionSummary;
  index: number;
}) {
  return (
    <div key={`device-${index}`}>
      <div className="flex justify-between">
        <span className="font-bold underline">POS DEVICE {index + 1}</span>
        <span />
      </div>
      <div className="flex justify-between">
        <span>Cash Day Start</span>
        <span>${device.cashCollected}</span>
      </div>
      <div className="flex justify-between">
        <span className="pl-4 italic">
          Input By: {device.inputByEmployeeId}
        </span>
        <span />
      </div>
      <div className="flex justify-between">
        <span>Cash Collected</span>
        <span>${device.cashCollected}</span>
      </div>
      <div className="flex justify-between">
        <span>Cash Sales</span>
        <span>${device.cashSales}</span>
      </div>
      <div className="flex justify-between">
        <span>Cash Deposits</span>
        <span>${device.cashDeposits}</span>
      </div>
      <div className="flex justify-between">
        <span>Cash Withdrawals</span>
        <span>${device.cashWithdrawals}</span>
      </div>
      <div className="flex justify-between">
        <span>Difference</span>
        <span>${device.difference}</span>
      </div>
    </div>
  );
}

// Devices Summary Section
function DevicesSummarySection({
  deviceTransactions,
  showDividers,
}: {
  deviceTransactions: TransactionSummary | TransactionSummary[];
  showDividers: boolean;
}) {
  const devices = Array.isArray(deviceTransactions)
    ? deviceTransactions
    : [deviceTransactions];

  return (
    <div>
      <div className="text-center font-bold">DEVICES SUMMARY</div>
      {devices.map((device, index) => (
        <DeviceTransactionSection
          device={device}
          index={index}
          key={`device-${index.toString()}-${device.cashCollected}`}
        />
      ))}
      {showDividers && <hr className="border-border my-2 border-t" />}
    </div>
  );
}

// Reason Tracking Section (Voided, Discounted, Refunded items)
function ReasonTrackingSection({
  title,
  items,
  totalLabel,
  keyPrefix,
  showManagerCodes,
  showDividers,
}: {
  title: string;
  items: ReasonTracking[];
  totalLabel: string;
  keyPrefix: string;
  showManagerCodes: boolean;
  showDividers: boolean;
}) {
  return (
    <div>
      <div className="text-center font-bold">{title}</div>
      {items.map((item: ReasonTracking) => (
        <div
          className="flex justify-between"
          key={`${keyPrefix}-${item.reason}-${item.amount}`}
        >
          <span>{item.reason}</span>
          <span>${item.amount}</span>
        </div>
      ))}
      <div className="flex justify-between font-bold">
        <span className="pl-4">{totalLabel}</span>
        <span>${calculateItemsTotal(items)}</span>
      </div>
      {showManagerCodes &&
        items.map((item: ReasonTracking) => (
          <div
            className="flex justify-between"
            key={`${keyPrefix}-code-${item.reason}-${item.permissionCode.employeeId}`}
          >
            <span className="italic">
              Permission Code ({item.permissionCode.employeeName})
            </span>
            <span>{item.permissionCode.employeeId}</span>
          </div>
        ))}
      {showDividers && <hr className="border-border my-2 border-t" />}
    </div>
  );
}

// Full report preview panel
export function BusinessReportPreview({
  locationName,
  address,
  currentDate,
  isPreviewData,
  isLoadingReport,
  reportError,
  hasRealData,
  displayData,
  visibilitySettings,
  showManagerCodes,
  showDividers,
}: {
  locationName: string;
  address: {
    addressLine1: string;
    addressLine2: string | null;
    city: string;
    state: string;
    zipCode: number;
  } | null;
  currentDate: Date;
  isPreviewData: boolean;
  isLoadingReport: boolean;
  reportError: Error | null;
  hasRealData: boolean;
  displayData: BusinessReportData;
  visibilitySettings: VisibilitySettings;
  showManagerCodes: boolean;
  showDividers: boolean;
}) {
  const {
    shouldShowOrdersBills,
    shouldShowSalesCategories,
    shouldShowSalesTaxes,
    shouldShowDevices,
    shouldShowVoided,
    shouldShowDiscounted,
    shouldShowRefunded,
  } = computeVisibility(visibilitySettings, isPreviewData, displayData);

  return (
    <div className="bg-background-subtle rounded-lg p-6">
      <div className="caption bg-background text-foreground max-h-dvh w-96 overflow-y-auto rounded-md px-6 py-8 font-mono shadow-md">
        <ReportHeader
          address={address}
          currentDate={currentDate}
          hasRealData={hasRealData}
          isLoadingReport={isLoadingReport}
          isPreviewData={isPreviewData}
          locationName={locationName}
          reportError={reportError}
        />

        {showDividers && <hr className="border-border my-2 border-t" />}

        {shouldShowOrdersBills && (
          <OrdersBillsSummarySection
            billsClosed={displayData.billsClosed}
            ordersCreated={displayData.ordersCreated}
            showDividers={showDividers}
          />
        )}

        {shouldShowSalesCategories && (
          <SalesCategoriesSection
            salesByCategory={displayData.salesByCategory}
            showDividers={showDividers}
          />
        )}

        {shouldShowSalesTaxes && (
          <SalesTaxesSummarySection
            showDividers={showDividers}
            totalSales={displayData.totalSales}
            totalTaxes={displayData.totalTaxes}
          />
        )}

        {shouldShowDevices && (
          <DevicesSummarySection
            deviceTransactions={displayData.deviceTransactions}
            showDividers={showDividers}
          />
        )}

        {shouldShowVoided && (
          <ReasonTrackingSection
            items={displayData.voidedItems}
            keyPrefix="void"
            showDividers={showDividers}
            showManagerCodes={showManagerCodes}
            title="VOIDED ITEMS"
            totalLabel="Voided Items Totals"
          />
        )}

        {shouldShowDiscounted && (
          <ReasonTrackingSection
            items={displayData.discountedItems}
            keyPrefix="discount"
            showDividers={showDividers}
            showManagerCodes={showManagerCodes}
            title="DISCOUNTED ITEMS"
            totalLabel="Discounted Items Totals"
          />
        )}

        {shouldShowRefunded && (
          <ReasonTrackingSection
            items={displayData.refundedItems}
            keyPrefix="refund"
            showDividers={showDividers}
            showManagerCodes={showManagerCodes}
            title="REFUNDED ITEMS"
            totalLabel="Refunded Items Total"
          />
        )}
      </div>
    </div>
  );
}
